import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ScrollView,
  Linking,
  Alert,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';

interface HelpSupportModalProps {
  visible: boolean;
  onClose: () => void;
}

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'How do I clock in/out?',
    answer: 'Navigate to the Time Clock section from the dashboard. Tap the "Clock In" button when starting your shift and "Clock Out" when ending. Your location may be recorded for verification.',
  },
  {
    question: 'How do I view my schedule?',
    answer: 'Access your schedule from the Schedule tab in the navigation bar or from the Quick Actions on your dashboard.',
  },
  {
    question: 'How do I view my timesheet?',
    answer: 'Open the Time tab and switch to the Pay Period view. You can see your worked hours, pay period summary, and punch history there.',
  },
  {
    question: 'What if I forgot to clock in or out?',
    answer: 'Contact your supervisor or manager to request a manual time adjustment. They can add missing punches through the administrative system.',
  },
  {
    question: 'How do I reset my password?',
    answer: 'Tap "Forgot Password?" on the login screen and follow the instructions to reset your password via email.',
  },
];

const HelpSupportModal: React.FC<HelpSupportModalProps> = ({ visible, onClose }) => {
  const [expandedFaq, setExpandedFaq] = React.useState<number | null>(null);

  const handleEmailSupport = () => {
    Linking.openURL('mailto:support@sgagymnastics.com?subject=SGA Mobile App Support');
  };

  const handleCallSupport = () => {
    Alert.alert(
      'Call Support',
      'Would you like to call SGA support?',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Call', onPress: () => Linking.openURL('tel:+15551234567') },
      ]
    );
  };

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Icon name="close" size={28} color="#333333" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Help & Support</Text>
          <View style={styles.headerSpacer} />
        </View>

        <ScrollView style={styles.content}>
          {/* Contact Support */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Contact Support</Text>
            <Text style={styles.sectionDescription}>
              Need help? Our support team is here to assist you.
            </Text>

            <TouchableOpacity style={styles.contactButton} onPress={handleEmailSupport}>
              <View style={styles.contactIconContainer}>
                <Icon name="mail-outline" size={24} color="#007AFF" />
              </View>
              <View style={styles.contactInfo}>
                <Text style={styles.contactLabel}>Email Support</Text>
                <Text style={styles.contactValue}>support@sgagymnastics.com</Text>
              </View>
              <Icon name="chevron-forward" size={20} color="#C7C7CC" />
            </TouchableOpacity>

            <TouchableOpacity style={styles.contactButton} onPress={handleCallSupport}>
              <View style={styles.contactIconContainer}>
                <Icon name="call-outline" size={24} color="#007AFF" />
              </View>
              <View style={styles.contactInfo}>
                <Text style={styles.contactLabel}>Phone Support</Text>
                <Text style={styles.contactValue}>(555) 123-4567</Text>
              </View>
              <Icon name="chevron-forward" size={20} color="#C7C7CC" />
            </TouchableOpacity>
          </View>

          {/* FAQ Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Frequently Asked Questions</Text>

            {faqs.map((faq, index) => (
              <TouchableOpacity
                key={index}
                style={styles.faqItem}
                onPress={() => toggleFaq(index)}
                activeOpacity={0.7}>
                <View style={styles.faqHeader}>
                  <Text style={styles.faqQuestion}>{faq.question}</Text>
                  <Icon
                    name={expandedFaq === index ? 'chevron-up' : 'chevron-down'}
                    size={20}
                    color="#666666"
                  />
                </View>
                {expandedFaq === index && (
                  <Text style={styles.faqAnswer}>{faq.answer}</Text>
                )}
              </TouchableOpacity>
            ))}
          </View>

          {/* Support Hours */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Support Hours</Text>
            <View style={styles.hoursRow}>
              <Text style={styles.hoursDay}>Monday - Friday</Text>
              <Text style={styles.hoursTime}>8:00 AM - 6:00 PM CST</Text>
            </View>
            <View style={styles.hoursRow}>
              <Text style={styles.hoursDay}>Saturday</Text>
              <Text style={styles.hoursTime}>9:00 AM - 2:00 PM CST</Text>
            </View>
            <View style={styles.hoursRow}>
              <Text style={styles.hoursDay}>Sunday</Text>
              <Text style={styles.hoursTime}>Closed</Text>
            </View>
          </View>

          {/* Quick Tips */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Quick Tips</Text>
            <View style={styles.tipItem}>
              <Icon name="bulb-outline" size={20} color="#FF9500" />
              <Text style={styles.tipText}>
                Pull down to refresh on most screens to get the latest data.
              </Text>
            </View>
            <View style={styles.tipItem}>
              <Icon name="bulb-outline" size={20} color="#FF9500" />
              <Text style={styles.tipText}>
                Make sure location services are enabled for accurate clock in/out.
              </Text>
            </View>
            <View style={styles.tipItem}>
              <Icon name="bulb-outline" size={20} color="#FF9500" />
              <Text style={styles.tipText}>
                Keep the app updated for the best experience and new features.
              </Text>
            </View>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F8F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  closeButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333333',
  },
  headerSpacer: {
    width: 36,
  },
  content: {
    flex: 1,
  },
  section: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 16,
    paddingVertical: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333333',
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 15,
    color: '#666666',
    marginBottom: 16,
  },
  contactButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  contactIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F0F7FF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  contactInfo: {
    flex: 1,
  },
  contactLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333333',
  },
  contactValue: {
    fontSize: 14,
    color: '#666666',
    marginTop: 2,
  },
  faqItem: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  faqHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  faqQuestion: {
    flex: 1,
    fontSize: 16,
    fontWeight: '500',
    color: '#333333',
    paddingRight: 8,
  },
  faqAnswer: {
    fontSize: 15,
    color: '#666666',
    lineHeight: 22,
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0',
  },
  hoursRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  hoursDay: {
    fontSize: 16,
    color: '#333333',
  },
  hoursTime: {
    fontSize: 16,
    color: '#666666',
  },
  tipItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  tipText: {
    flex: 1,
    fontSize: 15,
    color: '#666666',
    marginLeft: 12,
    lineHeight: 20,
  },
});

export default HelpSupportModal;
